import React, { useState, useEffect, useRef, useMemo, Suspense } from 'react';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import { CardState, CharacterCard, ExifData } from '../types';
import { logger } from '../utils/secureLogger';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { useDailyLimits } from '../hooks/useDailyLimits';
import { parseExifDate } from '../utils/dataUtils';
import { processFirestoreCardsLight, measureCardProcessing } from '../utils/cardUtils';
import { createDataUrl } from '../utils/imageCompression';
import { getCardDisplayDate, sortCardsForCalendar } from '../utils/cardDateUtils';
import { groupCardsByMonth } from '../utils/calendarUtils';
import { OWNER_UID } from '../constants/owner';
import { setCardsCache, getFeedCache, setFeedCache } from '../utils/cardCache';
import { navigate } from './Router';
import Header from './Header';
import { cardSaveService } from '../services/CardSaveService';
import UnifiedFloatingAddButton from './UnifiedFloatingAddButton';
import CalendarView from './CalendarView';

import Card from './Card';
import SkeletonCard from './SkeletonCard';

const PhotoUploadPage = React.lazy(() => import('./PhotoUploadPage'));

type ViewMode = 'feed' | 'calendar';

interface FeedPageProps {
  initialMonth?: string;
}

const FeedPage: React.FC<FeedPageProps> = ({ initialMonth }) => {
  const { currentUser } = useAuth();
  const { showToast } = useToast();
  const { checkLimit } = useDailyLimits();
  // 前回マウント時のフィード状態があれば即時復元（詳細ページから戻った時の再取得を防ぐ）
  const [allCards, setAllCards] = useState<CharacterCard[]>(() => getFeedCache()?.allCards ?? []);
  const getCurrentMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  };
  const [selectedMonth, setSelectedMonth] = useState(() => initialMonth || getCurrentMonth());

  // URLのinitialMonth変化（ロゴクリックで/に戻る等）に追従
  useEffect(() => {
    setSelectedMonth(initialMonth || getCurrentMonth());
  }, [initialMonth]);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    return (localStorage.getItem('viewMode') as ViewMode) || 'feed';
  });

  const [showUploadPage, setShowUploadPage] = useState(false);
  const [isLoading, setIsLoading] = useState(() => !getFeedCache());

  const isFetchingRef = useRef<boolean>(false);

  // 月タブ: 2025-07以降の全月を静的に生成（ほぼ毎日投稿しているため動的導出は不要）
  const FIRST_TAB_MONTH = '2025-07';
  const monthTabs = useMemo(() => {
    const tabs: { id: string; label: string }[] = [];
    const now = new Date();
    let y = now.getFullYear();
    let m = now.getMonth() + 1;
    while (true) {
      const id = `${y}-${String(m).padStart(2, '0')}`;
      if (id < FIRST_TAB_MONTH) break;
      tabs.push({ id, label: id.replace('-', '/') });
      m--;
      if (m === 0) { m = 12; y--; }
    }
    return tabs;
  }, []);

  // allCardsから選択月のカードを抽出（表示日付=EXIF優先）
  const cards = useMemo(() => {
    return allCards.filter(card => {
      const date = getCardDisplayDate(card);
      const cardMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      return cardMonth === selectedMonth;
    });
  }, [selectedMonth, allCards]);

  // ビューモード切り替え
  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    localStorage.setItem('viewMode', mode);
  };

  // ログアウト成功のトーストメッセージを表示
  useEffect(() => {
    const logoutSuccess = localStorage.getItem('logout_success');
    if (logoutSuccess === 'true') {
      showToast('Signed out successfully', 'success');
      localStorage.removeItem('logout_success');
    }
  }, [showToast]);

  // EXIFソート
  const filteredCards = useMemo(() => {
    return sortCardsForCalendar(cards);
  }, [cards]);

  // カレンダービュー用のグループ化
  const calendarMonths = useMemo(() => {
    if (viewMode !== 'calendar') return [];
    return groupCardsByMonth(filteredCards);
  }, [filteredCards, viewMode]);

  const fetchedMonthsRef = useRef<Set<string>>(new Set(getFeedCache()?.fetchedMonths ?? []));

  // 指定月のカードをFirestoreから取得
  const fetchCardsByMonth = async (month: string) => {
    if (isFetchingRef.current) return;
    if (!db) { setIsLoading(false); return; }
    // 既に取得済みの月はスキップ
    if (fetchedMonthsRef.current.has(month)) return;

    try {
      isFetchingRef.current = true;
      setIsLoading(true);

      const [year, mon] = month.split('-').map(Number);
      const startOfMonth = new Date(year, mon - 1, 1);
      // EXIF日付はこの月だが翌月に入ってからアップロードされたカードも拾えるよう、7日分の余裕を持たせる
      const endWithMargin = new Date(year, mon, 8);
      const cardsQuery = query(
        collection(db, "cards"),
        where('userId', '==', OWNER_UID),
        where('createdAt', '>=', startOfMonth),
        where('createdAt', '<', endWithMargin),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(cardsQuery);
      const fetched = measureCardProcessing(
        () => processFirestoreCardsLight(querySnapshot.docs, { includePrivate: true }),
        `Processing ${querySnapshot.docs.length} cards`
      );

      fetchedMonthsRef.current.add(month);

      // 既存カードとマージ（重複排除）
      setAllCards(prev => {
        const existingIds = new Set(prev.map(c => c.id));
        const newCards = fetched.filter(c => !existingIds.has(c.id));
        return [...prev, ...newCards];
      });
      setCardsCache(fetched);
    } catch (error) {
      logger.error("Error fetching cards from database:", error);
    } finally {
      isFetchingRef.current = false;
      setIsLoading(false);
    }
  };

  // 月変更時にフェッチ（未取得の月のみ）
  useEffect(() => { fetchCardsByMonth(selectedMonth); }, [selectedMonth]);

  // フィード状態をモジュールキャッシュへ保存（詳細ページから戻った時に即時復元するため）
  useEffect(() => {
    setFeedCache(allCards, Array.from(fetchedMonthsRef.current));
  }, [allCards]);

  // モーダル制御
  const handleUploadSelect = () => setShowUploadPage(true);
  const handleCloseAll = () => setShowUploadPage(false);

  const handleUploadPageImageSelect = (file: File, generateDescription: boolean = true) => {
    handleImageSelect(file);
    handleCloseAll();
  };

  // 写真アップロード処理
  const handleImageSelect = async (file: File) => {
    if (!file || !currentUser) return;

    if (checkLimit('photoUpload')) {
      showToast("You've reached today's pixel art creation limit. Try again tomorrow!", 'error');
      return;
    }

    const newCardId = `${Date.now()}`;
    let exif: ExifData | null = null;
    try {
      const { default: exifr } = await import('exifr');
      const data = await exifr.parse(file);
      if (data) {
        const rawCreateDate = data.DateTimeOriginal || data.CreateDate || data.DateTime || data.DateTimeDigitized || data.ModifyDate;
        let createDate = parseExifDate(rawCreateDate);
        if (!createDate && file.lastModified) createDate = new Date(file.lastModified);
        exif = { latitude: data.latitude, longitude: data.longitude, createDate, model: data.Model };
      }
    } catch (e) {
      logger.silent("Could not parse EXIF data.", e);
    }

    if (!exif && file.lastModified) {
      exif = { createDate: new Date(file.lastModified) };
    }

    const newCard: CharacterCard = {
      id: newCardId,
      userId: currentUser.id,
      originalFile: file,
      originalImageUrl: URL.createObjectURL(file),
      characterData: null,
      exifData: exif,
      pixelArtUrl: null,
      status: CardState.LOADING_DESCRIPTION,
      error: null,
      createdAt: new Date(),
      updatedAt: new Date(),
      generateDescription: true,
    };

    setAllCards(prevCards => [newCard, ...prevCards]);
    setTimeout(() => handleGenerate(newCardId, newCard), 100);
  };

  const handleGenerate = async (cardId: string, cardDirect?: CharacterCard) => {
    const cardToProcess = cardDirect || cards.find(c => c.id === cardId);
    if (!cardToProcess || !cardToProcess.originalFile) return;

    const updateCardState = (updates: Partial<CharacterCard>) => {
      setAllCards(prevCards => prevCards.map(c => c.id === cardId ? { ...c, ...updates } : c));
    };

    updateCardState({ status: CardState.LOADING_DESCRIPTION, error: null });

    try {
      const { generateCharacterDescription, generatePixelArt } = await import('../services/geminiService');
      const { mergeGenerationMetadata } = await import('../utils/generationMetadata');
      const { characterData: data, generationMetadata: analysisMetadata } =
        await generateCharacterDescription(cardToProcess.originalFile, cardToProcess.exifData);
      updateCardState({ characterData: data, generationMetadata: analysisMetadata, status: CardState.LOADING_IMAGE });

      const { imageBytes: imageBase64, generationMetadata: imageMetadata } =
        await generatePixelArt(data.description, data.cardType);
      const tempCardState: CharacterCard = {
        ...cardToProcess,
        characterData: data,
        // analyze 段階（analysis/diary）と generate 段階（image）を統合。どちらか欠けても残りは保持
        generationMetadata: mergeGenerationMetadata(analysisMetadata, imageMetadata),
        pixelArtUrl: createDataUrl(imageBase64),
        status: CardState.SUCCESS,
      };
      updateCardState(tempCardState);

      try {
        const savedCard = await cardSaveService.saveCard(tempCardState, imageBase64, {
          generateDescription: cardToProcess.generateDescription !== false,
          saveOriginalImage: true
        });
        updateCardState({ ...savedCard, originalImageUrl: undefined });
        setTimeout(() => { if (!isFetchingRef.current) { fetchedMonthsRef.current.delete(selectedMonth); fetchCardsByMonth(selectedMonth); } }, 1000);
      } catch (saveError: any) {
        let saveErrorMessage = "Failed to save";
        if (saveError.message) {
          if (saveError.message.includes('permission')) saveErrorMessage = "Permission denied to save card.";
          else if (saveError.message.includes('network')) saveErrorMessage = "Network error while saving.";
          else if (saveError.message.includes('corrupted') || saveError.message.includes('KB')) saveErrorMessage = "Image corrupted during compression.";
          else saveErrorMessage = `Save failed: ${saveError.message.substring(0, 50)}`;
        }
        updateCardState({ error: saveErrorMessage, status: CardState.ERROR });
      }
    } catch (err: unknown) {
      let errorMessage = "An error occurred";
      if (err instanceof Error) {
        if (err.message.includes('Rate limit exceeded')) errorMessage = "Daily limit reached. Please try again tomorrow.";
        else if (err.message.includes('quota')) errorMessage = "API quota exceeded. Please try again later.";
        else if (err.message.includes('corrupted') || err.message.includes('incomplete')) errorMessage = "Image generation failed: Corrupted data.";
        else if (err.message.includes('Authentication')) errorMessage = "Authentication required. Please sign in again.";
        else if (err.message.includes('Network') || err.message.includes('fetch')) errorMessage = "Network error. Please check your connection.";
        else if (err.message.includes('safety') || err.message.includes('policy')) errorMessage = "Content policy violation. Try a different image.";
        else errorMessage = err.message;
      }
      updateCardState({ error: errorMessage, status: CardState.ERROR });
    }
  };

  const handleDeleteCard = async (cardId: string) => {
    try {
      const cardToDelete = cards.find(card => card.id === cardId);
      if (db && cardToDelete && cardToDelete.status === CardState.SUCCESS && currentUser) {
        await cardSaveService.deleteCard(cardId, cardToDelete, currentUser.id);
      }
      setAllCards(prevCards => prevCards.filter(card => card.id !== cardId));
    } catch (error) {
      // エラーハンドリング
    }
  };

  return (
    <div className="min-h-screen p-4 sm:p-8 pb-16 sm:pb-20 font-sans flex flex-col bg-theme-primary">
      <Header />

      <main className="flex-grow">
        {/* ビュー切り替え + 年月タブ */}
        <div className="flex items-center mb-6 -mr-4 sm:-mr-8">
          {/* ビュー切り替え */}
          <div className="flex-shrink-0 flex items-center space-x-1 bg-theme-secondary rounded-lg p-1 mr-3">
            <button
              onClick={() => handleViewModeChange('feed')}
              aria-label="Grid view"
              className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                viewMode === 'feed'
                  ? 'bg-theme-primary text-theme-primary shadow-sm'
                  : 'text-theme-tertiary hover:text-theme-secondary'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
            <button
              onClick={() => handleViewModeChange('calendar')}
              aria-label="Calendar view"
              className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                viewMode === 'calendar'
                  ? 'bg-theme-primary text-theme-primary shadow-sm'
                  : 'text-theme-tertiary hover:text-theme-secondary'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </button>
          </div>

          {/* 年月タブ - 右端まではみ出す */}
          <div className="flex overflow-x-auto scrollbar-hide space-x-2 pr-4 sm:pr-8">
            {monthTabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => {
                  setSelectedMonth(tab.id);
                  navigate(`/${tab.id.replace('-', '/')}`);
                }}
                className={`flex-shrink-0 px-3 py-1.5 text-sm rounded-full transition-colors ${
                  selectedMonth === tab.id
                    ? 'bg-brand-primary text-white'
                    : 'bg-theme-secondary text-theme-secondary hover:bg-theme-tertiary'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* コンテンツ */}
        {isLoading && cards.length === 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-8">
            {Array.from({ length: 10 }).map((_, index) => (
              <SkeletonCard key={`skeleton-${index}`} />
            ))}
          </div>
        ) : viewMode === 'calendar' ? (
          <CalendarView
            monthGroups={calendarMonths}
            onGenerate={handleGenerate}
            onDelete={handleDeleteCard}
            isOwnPage={currentUser?.id === OWNER_UID}
          />
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-8">
            {filteredCards.map((card) => (
              <Card
                key={card.id}
                card={card}
                onGenerate={handleGenerate}
                onDelete={handleDeleteCard}
              />
            ))}
          </div>
        )}
      </main>

      <UnifiedFloatingAddButton
        mode="custom"
        onCustomClick={handleUploadSelect}
        customTitle="Add new photo"
      />

      {/* Photo Upload Modal */}
      {showUploadPage && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-theme-primary rounded-lg w-full max-w-[calc(100vw-2rem)] md:max-w-[28rem] max-h-[90vh] overflow-auto">
            <Suspense fallback={<div className="flex items-center justify-center p-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div></div>}>
              <PhotoUploadPage
                onClose={handleCloseAll}
                onImageSelect={handleUploadPageImageSelect}
              />
            </Suspense>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedPage;
